import { useEffect, useMemo, useState } from 'react';
import { DataGrid, KeenIcon } from '@/components';
import usePagination from '@/hooks/usePagination';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useDeleteRegisteredDMOMutation,
  useGetRegisteredDMOsQuery,
} from '@/services/DMOManagementService';
import { PaginatedApiResponse } from '@/types/api.types';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import {
  DMORegisteredProfile,
  DmoRegistrationStatus,
} from '@/types/dmo-management.types';
import { useSnackbar } from 'notistack';
import { ConfirmationDialog } from '@/components/dialogBox/ConfirmationDialog';
import { formatDisplayDate } from '@/utils/Date';

interface DMOUserRow {
  id: string;
  name: string;
  email: string;
  status: string;
  profile_pic: string | null;
  created_at: string;
}

const DMOUsersList = () => {
  const { hasPermission } = usePermissions();
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();

  const [dataKey, setDataKey] = useState(() => Date.now());
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [deleteDmo, { isLoading: isDeleting }] =
    useDeleteRegisteredDMOMutation();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedDmoId, setSelectedDmoId] = useState<string | null>(null);

  const queryParams = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: debouncedValue,
      language: 'en',
    }),
    [pagination.pageIndex, pagination.pageSize, debouncedValue]
  );

  const { data, isLoading, isFetching, isError, refetch } =
    useGetRegisteredDMOsQuery(queryParams) as {
      data: PaginatedApiResponse<DMORegisteredProfile[]> | undefined;
      isLoading: boolean;
      isFetching: boolean;
      isError: boolean;
      refetch: () => void;
    };

  useEffect(() => {
    if (data) {
      handleTotalPages(data.totalPages || 1);
      handleTotalItems(data.totalItems || 0);
    }
  }, [data, handleTotalPages, handleTotalItems]);

  const resetFilters = () => {
    if (searchTerm) {
      resetSearch();
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      setDataKey(Date.now());
      refetch();
    }
  };

  const handleDelete = async () => {
    if (!selectedDmoId) return;

    try {
      await deleteDmo(selectedDmoId).unwrap();
      enqueueSnackbar('DMO user deleted successfully', { variant: 'success' });
      refetch();
      setIsDeleteDialogOpen(false);
      setSelectedDmoId(null);
    } catch (error) {
      console.error('Failed to delete DMO user:', error);
      enqueueSnackbar('Failed to delete DMO user', { variant: 'error' });
    }
  };

  const columns: ColumnDef<DMOUserRow>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        enableSorting: false,
        cell: ({ row }) => {
          const canViewDetails = hasPermission(
            'dmo_registration_management_read'
          );

          const handleViewDetails = () => {
            if (!canViewDetails) return;
            navigate(`/dmo-management/details/${row.original.id}`);
          };

          return (
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-9 h-9 rounded-full bg-gray-100">
                {row.original.profile_pic ? (
                  <img
                    src={row.original.profile_pic}
                    alt={row.original.name || ''}
                    className="w-9 h-9 rounded-full object-cover"
                  />
                ) : (
                  <KeenIcon
                    type="filled"
                    icon="user"
                    className="text-gray-500 text-lg"
                  />
                )}
              </div>
              <div className="flex flex-col">
                {canViewDetails ? (
                  <button
                    type="button"
                    onClick={handleViewDetails}
                    className="text-sm font-medium text-blue-600 contrast:text-blue-200 hover:text-blue-800 text-left focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    aria-label={`View details for ${row.original.name}`}
                  >
                    {row.original.name || '-'}
                  </button>
                ) : (
                  <span className="text-sm font-medium text-gray-900">
                    {row.original.name || '-'}
                  </span>
                )}
                <span className="text-xs text-gray-500">
                  {row.original.email}
                </span>
              </div>
            </div>
          );
        },
        meta: { className: 'min-w-[260px]' },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => {
          const status = String(row.original.status);
          const isActive =
            status === DmoRegistrationStatus.ACTIVE || status === 'Active';
          const label = isActive ? 'Active' : 'Inactive';

          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
            >
              {label}
            </span>
          );
        },
        meta: { className: 'min-w-[120px]' },
      },
      {
        accessorKey: 'created_at',
        header: 'Created At',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-sm text-gray-700">
            {formatDisplayDate(new Date(row.original.created_at))}
          </span>
        ),
        meta: { className: 'min-w-[140px]' },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            {hasPermission('dmo_registration_management_read') && (
              <button
                type="button"
                onClick={() =>
                  navigate(`/dmo-management/details/${row.original.id}`)
                }
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="View"
                aria-label={`View ${row.original.name}`}
              >
                <KeenIcon type="filled" icon="eye" className="text-gray-500" />
              </button>
            )}
            {hasPermission('dmo_registration_management_update') && (
              <button
                type="button"
                onClick={() =>
                  navigate(`/dmo-management/register/${row.original.id}`)
                }
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="Edit"
                aria-label={`Edit ${row.original.name}`}
              >
                <KeenIcon
                  type="filled"
                  icon="pencil"
                  className="text-gray-500"
                />
              </button>
            )}
            {hasPermission('dmo_registration_management_delete') && (
              <button
                type="button"
                onClick={() => {
                  setSelectedDmoId(row.original.id);
                  setIsDeleteDialogOpen(true);
                }}
                className="btn btn-sm btn-icon btn-light btn-outline text-red-500 hover:text-red-700"
                title="Delete"
                aria-label={`Delete ${row.original.name}`}
              >
                <KeenIcon type="filled" icon="trash" className="text-red-500" />
              </button>
            )}
          </div>
        ),
        meta: { className: 'w-[120px]' },
      },
    ],
    [navigate, hasPermission]
  );

  if (!hasPermission('dmo_registration_management_read')) {
    return (
      <div className="bg-gray-100 p-6 rounded-lg shadow-lg flex flex-col">
        <div className="text-center py-4 text-gray-500" role="alert">
          You don't have permission to view DMO users.
        </div>
      </div>
    );
  }

  return (
    <div className="card card-grid w-full">
      <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 justify-between">
        <div className="flex items-center gap-2">
          <label
            className="input input-sm h-9 flex items-center"
            htmlFor="search-dmo-users"
            aria-label="Search DMO users"
          >
            <KeenIcon
              type="filled"
              icon="magnifier"
              className="text-gray-500"
            />
            <input
              id="search-dmo-users"
              placeholder="Search DMO users"
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              className="w-full sm:w-56 h-9 text-sm"
              aria-label="Search DMO users"
            />
          </label>
          {searchTerm && (
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={resetFilters}
            >
              Reset
            </button>
          )}
        </div>
      </div>

      <div className="card-body">
        {isError ? (
          <div className="text-center py-4 text-red-500">
            Failed to load DMO users. Please try again.
          </div>
        ) : (
          <DataGrid
            key={dataKey}
            columns={columns}
            data={data?.data || []}
            rowSelect={false}
            loading={isLoading || isFetching}
            pagination={{
              page: pagination.pageIndex,
              size: pagination.pageSize,
              more: true,
              totalItems,
              totalPages,
              setPagination,
              info: '{from} - {to} of {count}',
              sizes: [5, 10, 25, 50, 100],
              sizesLabel: 'Show',
              sizesDescription: 'per page',
            }}
            serverSide={true}
            onFilterReset={resetFilters}
            hasActiveFilters={!!searchTerm}
            aria-label="Registered DMO users table"
          />
        )}
      </div>

      <ConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setSelectedDmoId(null);
        }}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this DMO user? This action cannot be undone."
        confirmButtonText="Delete"
        confirmButtonVariant="destructive"
        isLoading={isDeleting}
      />
    </div>
  );
};

export { DMOUsersList };
